import { Action, createReducer, on } from '@ngrx/store';
import { BoxxGenericState, getGenericInitialState } from '@boxx/core';
import { LeadModel, ReviewModel } from '../..';
import {
  IMessagesStateError,
  IMessagesStateSuccess,
} from '../core/IStateErrorSuccess';
import {
  GBMConversationItemModel,
  GBMMessageItemModel,
} from '../models/GBMessage.model';
import { MessageSource } from '../models/Message.model';
import * as fromGbmActions from './actions/gbmConversations.actions';
import * as fromLeadsActions from './actions/leads.actions';
import * as fromReviewsActions from './actions/reviews.actions';
import * as fromNotificationsActions from './actions/notifications.actions';
import * as fromActions from './messages.actions';

export interface IReviewsStateData
  extends BoxxGenericState<
    ReviewModel[],
    IMessagesStateSuccess,
    IMessagesStateError
  > {
  totals: {
    averageRating: number;
    totalReviewCount: number;
  };
}

export interface IGbmMessagesStateData {
  conversations: BoxxGenericState<
    GBMConversationItemModel[],
    IMessagesStateSuccess,
    IMessagesStateError
  >;
  seletedConversationId: number | string;
  messages: BoxxGenericState<
    GBMMessageItemModel[],
    IMessagesStateSuccess,
    IMessagesStateError
  >;
}

export interface IGMessagesStateData
  extends BoxxGenericState<
    any[],
    IMessagesStateSuccess,
    IMessagesStateError
  > {
  totals: {
    averageRating: number;
    totalReviewCount: number;
  };
}

export interface MessagesState {
  leadsState: BoxxGenericState<
    LeadModel[],
    IMessagesStateSuccess,
    IMessagesStateError
  >;
  reviewsState: IReviewsStateData;
  gbmConversationsState: IGbmMessagesStateData;
  notificationsState: IGMessagesStateData;

  filteredSources: MessageSource[];
  filteredItems: (GBMConversationItemModel & LeadModel & ReviewModel)[];
  sorting: 'ASC' | 'DESC';
  selectedId: number | string;
}

const initialState: MessagesState = {
  leadsState: {
    ...getGenericInitialState(),
    data: [],
  },
  reviewsState: {
    ...getGenericInitialState(),
    totals: { totalReviewCount: 0, averageRating: 0 },
    data: [],
  },
  gbmConversationsState: {
    conversations: {
      ...getGenericInitialState(),
      data: [],
    },
    seletedConversationId: null,
    messages: {
      ...getGenericInitialState(),
      data: [],
    },
  },
  notificationsState: {
    ...getGenericInitialState(),
    totals: { totalReviewCount: 0, averageRating: 0 },
    data: [],
  },

  filteredSources: [],
  filteredItems: [],
  sorting: 'ASC',
  selectedId: null,
};

const reducer = createReducer(
  initialState,

  // -------- CONVERSATIONS ------------
  on(
    fromGbmActions.getGbmConversationsBeginAction,
    (state): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        conversations: {
          ...state.gbmConversationsState.conversations,
          isLoading: true,
          error: null,
          success: null,
        },
      },
    })
  ),
  on(
    fromGbmActions.getGbmConversationsSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        conversations: {
          ...state.gbmConversationsState.conversations,
          data: [...payload],
          isLoading: false,
          error: null,
          success: { after: 'GET' },
          fetched: true,
        },
      },
    })
  ),
  on(
    fromGbmActions.getGbmConversationsFailAction,
    (state, action): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        conversations: {
          ...state.gbmConversationsState.conversations,
          //rewrite data in order to trigger component update
          data: [...state.gbmConversationsState.conversations.data],
          isLoading: false,
          success: null,
          error: { after: 'GET', error: action.errors },
          fetched: true,
        },
      },
    })
  ),
  on(
    fromGbmActions.getGbmConversationMessagesBeginAction,
    (state, { payload }): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        seletedConversationId: payload,
        messages: {
          ...state.gbmConversationsState.messages,
          isLoading: true,
          error: null,
          success: null,
        },
      },
      selectedId: payload,
    })
  ),
  on(
    fromGbmActions.getGbmConversationMessagesSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        messages: {
          ...state.gbmConversationsState.messages,
          data: payload,
          isLoading: false,
          success: { after: 'GET' },
        },
      },
    })
  ),
  on(
    fromGbmActions.getGbmConversationMessagesFailAction,
    (state, { errors }): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        messages: {
          ...state.gbmConversationsState.messages,
          data: [...state.gbmConversationsState.messages.data],
          isLoading: false,
          error: { after: 'GET', error: errors },
        },
      },
    })
  ),

  on(
    fromGbmActions.replyGbmConversationMessageBeginAction,
    (state): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        messages: {
          ...state.gbmConversationsState.messages,
          isLoading: true,
          error: null,
          success: null,
        },
      },
    })
  ),
  on(
    fromGbmActions.replyGbmConversationMessageSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        messages: {
          ...state.gbmConversationsState.messages,
          data: [...payload],
          isLoading: false,
          error: null,
          success: { after: 'REPLY_CONVERSATION_MESSAGE' },
        },
      },
    })
  ),
  on(
    fromGbmActions.replyGbmConversationMessageFailAction,
    (state, { errors }): MessagesState => ({
      ...state,
      gbmConversationsState: {
        ...state.gbmConversationsState,
        messages: {
          ...state.gbmConversationsState.messages,
          isLoading: false,
          error: { after: 'REPLY_CONVERSATION_MESSAGE', error: errors },
          success: null,
        },
      },
    })
  ),

  // ----------- LEADS --------------------
  on(
    fromLeadsActions.getLeadsBeginAction,
    (state): MessagesState => ({
      ...state,
      leadsState: {
        ...state.leadsState,
        error: null,
        success: null,
        isLoading: true,
      },
    })
  ),
  on(
    fromLeadsActions.setLeadAsReadBeginAction,
    (state): MessagesState => ({
      ...state,
      leadsState: {
        ...state.leadsState,
        error: null,
        success: null,
        // isLoading: true, //<-- commented out or === false because I dont want to show any loader
      },
    })
  ),
  on(
    fromLeadsActions.getLeadsSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      leadsState: {
        ...state.leadsState,
        data: payload,
        isLoading: false,
        fetched: true,
        error: null,
        success: { after: 'GET' },
      },
    })
  ),
  on(
    fromLeadsActions.getLeadsFailAction,
    (state, { errors }): MessagesState => ({
      ...state,
      leadsState: {
        ...state.leadsState,
        data: [...(state.leadsState.data || [])],
        success: null,
        isLoading: false,
        error: { after: 'GET', error: errors },
        fetched: true,
      },
    })
  ),
  on(
    fromLeadsActions.setLeadAsReadSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      leadsState: {
        ...state.leadsState,
        data: payload,
        error: null,
        // isLoading: false,// isLoading: true, //<-- commented out or === false because I dont want to show any loader
        success: { after: 'SET_LEAD_READ' },
      },
    })
  ),
  on(
    fromLeadsActions.setLeadAsReadFailAction,
    (state, { errors }): MessagesState => ({
      ...state,
      leadsState: {
        ...state.leadsState,
        success: null,
        error: { after: 'SET_LEAD_READ', error: errors },
      },
    })
  ),

  // --------------- REVIEWS ------------------------
  on(
    fromReviewsActions.getReviewsBeginAction,
    (state): MessagesState => ({
      ...state,
      reviewsState: {
        ...state.reviewsState,
        error: null,
        success: null,
        isLoading: true,
      },
    })
  ),
  on(
    fromReviewsActions.getReviewsSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      reviewsState: {
        ...state.reviewsState,
        totals: {
          averageRating: payload.averageRating,
          totalReviewCount: payload.totalReviewCount,
        },
        data: payload.reviews,
        error: null,
        success: { after: 'GET' },
        isLoading: false,
        fetched: true,
      },
    })
  ),
  on(
    fromReviewsActions.getReviewsFailAction,
    (state, { errors }): MessagesState => ({
      ...state,
      reviewsState: {
        ...state.reviewsState,
        totals: {
          averageRating: state.reviewsState.totals.averageRating ?? 0,
          totalReviewCount: state.reviewsState.totals.totalReviewCount ?? 0,
        },
        data: [...(state.reviewsState.data || [])],
        error: { after: 'GET', error: errors },
        success: null,
        isLoading: false,
        fetched: true,
      },
    })
  ),
  on(
    fromReviewsActions.upsertReviewReplyBeginAction,
    fromReviewsActions.deleteReviewReplyBeginAction,
    (state): MessagesState => ({
      ...state,
      reviewsState: {
        ...state.reviewsState,
        error: null,
        success: null,
        isLoading: true,
      },
    })
  ),

  on(
    fromReviewsActions.upsertReviewReplySuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      reviewsState: {
        ...state.reviewsState,
        data: payload,
        isLoading: false,
        error: null,
        success: { after: 'UPSERT_REVIEW' },
        fetched: true,
      },
    })
  ),
  on(
    fromReviewsActions.deleteReviewReplySuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      reviewsState: {
        ...state.reviewsState,
        data: payload,
        isLoading: false,
        error: null,
        success: { after: 'DELETE_REVIEW' },
      },

      /* pageData: {
        ...state.pageData,
        messages: state.pageData.messages.map((m) =>
          m.id === action.review.id ? (action.review as any) : (m as any)
        ),
      }, */
    })
  ),

  on(
    fromReviewsActions.upsertReviewReplyFaliAction,
    (state, { errors }): MessagesState => ({
      ...state,
      reviewsState: {
        ...state.reviewsState,
        isLoading: false,
        error: { after: 'UPSERT_REVIEW', error: errors },
      },
    })
  ),
  on(
    fromReviewsActions.deleteReviewReplyFaliAction,
    (state, { errors }): MessagesState => ({
      ...state,
      reviewsState: {
        ...state.reviewsState,
        isLoading: false,
        error: { after: 'DELETE_REVIEW', error: errors },
      },
    })
  ),

  // --------------- NOTIFICATIONS ------------------------ 
  on(
    fromNotificationsActions.getNotificationsBeginAction,
    (state): MessagesState => ({
      ...state,
      notificationsState: {
        ...state.notificationsState,
        error: null,
        success: null,
        isLoading: true,
      },
    })
  ),
  on(
    fromNotificationsActions.setNotificationAsReadBeginAction,
    (state): MessagesState => ({
      ...state,
      notificationsState: {
        ...state.notificationsState,
        error: null,
        success: null,
      },
    })
  ),
  on(
    fromNotificationsActions.getNotificationsSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      notificationsState: {
        ...state.notificationsState,
        totals: {
          averageRating: payload.averageRating,
          totalReviewCount: payload.totalReviewCount,
        },
        data: payload,
        error: null,
        success: { after: 'GET' },
        isLoading: false,
        fetched: true,
      },
    })
  ),
  on(
    fromNotificationsActions.getNotificationsFailAction,
    (state, { errors }): MessagesState => ({
      ...state,
      notificationsState: {
        ...state.notificationsState,
        totals: {
          averageRating: state.notificationsState.totals.averageRating ?? 0,
          totalReviewCount: state.notificationsState.totals.totalReviewCount ?? 0,
        },
        data: [...(state.reviewsState.data || [])],
        error: { after: 'GET', error: errors },
        success: null,
        isLoading: false,
        fetched: true,
      },
    })
  ),

  on(
    fromNotificationsActions.setNotificationAsReadSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      notificationsState: {
        ...state.notificationsState,
        data: payload,
        error: null,
        // isLoading: false,// isLoading: true, //<-- commented out or === false because I dont want to show any loader
        success: { after: 'SET_READ_NOTIFICATION' },
      },
    })
  ),
  on(
    fromNotificationsActions.setNotificationAsReadFailAction,
    (state, { errors }): MessagesState => ({
      ...state,
      notificationsState: {
        ...state.notificationsState,
        success: null,
        error: { after: 'SET_READ_NOTIFICATION', error: errors },
      },
    })
  ),

  // FILTER
  on(
    fromActions.filterMessagesByReadStatusSuccessAction,
    (state, { payload }): MessagesState => ({
      ...state,
      filteredItems: payload,
    })
  ),
  on(
    fromActions.filterMessagesBySourceSuccessAction,
    (state, action): MessagesState => ({
      ...state,
      filteredSources: action.sources,
      filteredItems: action.filteredMessages,
    })
  ),

  // SELECT
  on(
    fromActions.selectConversationAction,
    (state, action): MessagesState => ({
      ...state,
      selectedId: action.conversationId,
    })
  ),

  on(
    fromActions.selectMessageAction,
    (state, action): MessagesState => ({
      ...state,
      selectedId: action.messageId,
    })
  ),

  on(
    fromActions.clearConversationSuccessErrorDataAction,
    (state): MessagesState => ({
      ...state,
      leadsState: {
        ...state.leadsState,
        success: null,
        error: null,
      },
      reviewsState: {
        ...state.reviewsState,
        success: null,
        error: null,
      },
      gbmConversationsState: {
        ...state.gbmConversationsState,
        conversations: {
          ...state.gbmConversationsState.conversations,
          success: null,
          error: null,
        },
        messages: {
          ...state.gbmConversationsState.messages,
          success: null,
          error: null,
        },
      },
    })
  )
);

export const messagesReducer = (
  state: MessagesState | undefined,
  action: Action
) => reducer(state, action);
